import { useCallback, useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { Crown, Eye, Pencil, Trash2, UserPlus, Users } from 'lucide-react';
import type { Project, ProjectRole } from '../lib/types';
import {
  removeMember,
  setMemberRole,
  shareProject,
  shareProjectWithMember,
} from '../services/projects';
import { functions } from '../lib/firebase';
import { useAuth } from '../hooks/useAuth';
import { Badge, Button, Input, Modal, Select, cx } from './ui';
import LabSetup from './LabSetup';

interface LabMate {
  uid: string;
  email: string;
  displayName: string;
}

const callMates = httpsCallable<
  void,
  { ok: true; labCode: string | null; labName: string | null; mates: LabMate[] }
>(functions, 'listLabMates');

const ROLE_LABEL: Record<ProjectRole, string> = {
  owner: 'Owner',
  editor: 'Can edit',
  viewer: 'Can view',
};

const ROLE_ICON: Record<ProjectRole, typeof Crown> = {
  owner: Crown,
  editor: Pencil,
  viewer: Eye,
};

export default function ShareDialog({
  open,
  onClose,
  project,
  currentUid,
}: {
  open: boolean;
  onClose: () => void;
  project: Project;
  currentUid: string;
}) {
  const { user } = useAuth();
  const [mates, setMates] = useState<LabMate[]>([]);
  const [labName, setLabName] = useState<string | null>(null);
  const [labCode, setLabCode] = useState<string | null>(null);
  const [settingUpLab, setSettingUpLab] = useState(false);
  const [pickedUid, setPickedUid] = useState('');
  const [email, setEmail] = useState('');
  const [copiedSelf, setCopiedSelf] = useState(false);
  const [role, setRole] = useState<Exclude<ProjectRole, 'owner'>>('editor');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const loadMates = useCallback(async () => {
    try {
      const res = await callMates();
      setMates(res.data.mates ?? []);
      setLabName(res.data.labName || null);
      setLabCode(res.data.labCode || null);
    } catch {
      // Sharing by address still works, so a failure here is not worth a
      // message of its own — the dropdown simply does not appear.
      setMates([]);
    }
  }, []);

  useEffect(() => {
    if (open) void loadMates();
  }, [open, loadMates]);

  const isOwner = project.ownerUid === currentUid;
  const memberEntries = Object.entries(project.members ?? {}).sort(([, a], [, b]) =>
    a.role === 'owner' ? -1 : b.role === 'owner' ? 1 : 0,
  );

  /** Lab mates who are not already on this project. */
  const available = mates.filter((m) => !(project.memberUids ?? []).includes(m.uid));

  async function inviteMate(e: React.FormEvent) {
    e.preventDefault();
    const mate = available.find((m) => m.uid === pickedUid);
    if (!mate || busy) return;
    setBusy(true);
    setError(null);
    setSuccess(null);

    const result = await shareProjectWithMember(project, mate, role, currentUid);
    setBusy(false);

    if (result.ok) {
      setSuccess(
        `${mate.displayName || mate.email} can now open this project from their own account.`,
      );
      setPickedUid('');
      return;
    }
    if (result.reason === 'already-member') setError('They already have access to this project.');
    else if (result.reason === 'self') setError('That is your own account.');
    else setError('Could not share. Only the owner can add people.');
  }

  async function invite(e: React.FormEvent) {

    e.preventDefault();
    if (!email.trim() || busy) return;
    setBusy(true);
    setError(null);
    setSuccess(null);

    const result = await shareProject(project, email, role, currentUid);
    setBusy(false);

    if (result.ok) {
      setSuccess(`${email.trim()} can now open this project from their own account.`);
      setEmail('');
      return;
    }
    switch (result.reason) {
      case 'no-account':
        setError(
          'No BlisxBench account uses that address. Ask them to open the app and sign in with ' +
            'Google once — their account address then appears at the bottom of their own Share ' +
            'dialog, which is the one to use here.',
        );
        break;
      case 'already-member':
        setError('They already have access to this project.');
        break;
      case 'self':
        setError('That is your own account.');
        break;
      default:
        setError('Could not share. Only the owner can add people.');
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Share project">
      <p className="mb-4 text-sm text-ink-400">
        People you add see <strong className="text-ink-300">{project.name}</strong> in their own
        account, under Shared with me.
      </p>

      {isOwner && available.length > 0 && (
        <form onSubmit={inviteMate} className="mb-5 space-y-2">
          <label className="field-label" htmlFor="share-mate">
            Add someone from {labName || 'your lab'}
          </label>
          <Select
            id="share-mate"
            value={pickedUid}
            onChange={(e) => {
              setPickedUid((e.target as HTMLSelectElement).value);
              setError(null);
              setSuccess(null);
            }}
            className="w-full"
          >
            <option value="">Choose a colleague…</option>
            {available.map((m) => (
              <option key={m.uid} value={m.uid}>
                {m.displayName ? `${m.displayName} — ${m.email}` : m.email}
              </option>
            ))}
          </Select>
          <Select
            value={role}
            onChange={(e) =>
              setRole((e.target as HTMLSelectElement).value as Exclude<ProjectRole, 'owner'>)
            }
            className="w-full"
          >
            <option value="editor">Can edit — capture and change values</option>
            <option value="viewer">Can view — read and export only</option>
          </Select>
          <Button
            type="submit"
            variant="primary"
            className="w-full"
            loading={busy}
            disabled={!pickedUid}
            icon={<Users size={16} />}
          >
            Add to project
          </Button>
        </form>
      )}

      {isOwner && (
        <form onSubmit={invite} className="mb-5 space-y-2">
          <label className="field-label" htmlFor="share-email">
            {available.length > 0 ? 'Or add someone outside the lab' : 'Add someone by email'}
          </label>
          <p className="text-xs text-ink-400">
            Use the address they sign in with. They must have opened BlisxBench at least once
            before they can be added.
          </p>
          <Input
            id="share-email"
            type="email"
            inputMode="email"
            autoCapitalize="none"
            spellCheck={false}
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setError(null);
              setSuccess(null);
            }}
            placeholder="colleague@example.com"
            autoComplete="off"
            className="w-full text-base"
          />
          <Select
            value={role}
            onChange={(e) =>
              setRole((e.target as HTMLSelectElement).value as Exclude<ProjectRole, 'owner'>)
            }
            className="w-full"
          >
            <option value="editor">Can edit — capture and change values</option>
            <option value="viewer">Can view — read and export only</option>
          </Select>
          <Button
            type="submit"
            variant="primary"
            className="w-full"
            loading={busy}
            disabled={!email.trim()}
            icon={<UserPlus size={16} />}
          >
            Add to project
          </Button>

        </form>
      )}

      {isOwner && available.length === 0 && (
        <div className="mb-5 rounded-lg border border-ink-700 bg-ink-800/40 p-3">
          <p className="text-sm">
            {labCode
              ? `Nobody else has joined ${labName || 'your lab'} yet.`
              : 'You are not in a lab yet.'}
          </p>
          <p className="mt-1 text-xs text-ink-400">
            A lab groups your team so colleagues appear here by name, instead of everyone having to
            remember addresses. It costs nothing and changes nothing about what you pay.
          </p>
          <Button
            size="sm"
            variant="secondary"
            className="mt-2.5"
            icon={<Users size={14} />}
            onClick={() => setSettingUpLab(true)}
          >
            {labCode ? 'Invite your team' : 'Create a lab'}
          </Button>
        </div>
      )}

      <LabSetup
        open={settingUpLab}
        onClose={() => {
          setSettingUpLab(false);
          void loadMates();
        }}
        onCreated={() => void loadMates()}
      />

      {error && (
        <p className="mb-4 rounded-lg border border-red-800 bg-red-950 p-2.5 text-sm text-red-200">
          {error}
        </p>
      )}
      {success && (
        <p className="mb-4 rounded-lg border border-emerald-800 bg-emerald-950 p-2.5 text-sm text-emerald-200">
          {success}
        </p>
      )}

      <div>
        <p className="field-label mb-2">
          {memberEntries.length} {memberEntries.length === 1 ? 'person' : 'people'} with access
        </p>
        <ul className="space-y-1.5">
          {memberEntries.map(([uid, member]) => {
            const Icon = ROLE_ICON[member.role];
            const isSelf = uid === currentUid;
            return (
              <li
                key={uid}
                className="flex items-center gap-2 rounded-lg border border-ink-700 bg-ink-800/40 px-3 py-2"
              >
                <Icon
                  size={14}
                  className={cx('shrink-0', member.role === 'owner' ? 'text-amber-300' : 'text-ink-400')}
                />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm">
                    {member.displayName || member.email || uid}
                    {isSelf && <span className="ml-1 text-ink-400">(you)</span>}
                  </p>
                  {member.email && member.displayName && (
                    <p className="truncate text-xs text-ink-400">{member.email}</p>
                  )}
                </div>

                {member.role === 'owner' ? (
                  <Badge tone="warn">Owner</Badge>
                ) : isOwner ? (
                  <div className="flex shrink-0 items-center gap-1">
                    <Select
                      value={member.role}
                      onChange={(e) =>
                        void setMemberRole(
                          project.id,
                          uid,
                          (e.target as HTMLSelectElement).value as Exclude<ProjectRole, 'owner'>,
                        )
                      }
                      className="w-28 py-1 text-xs"
                    >
                      <option value="editor">Can edit</option>
                      <option value="viewer">Can view</option>
                    </Select>
                    <button
                      onClick={() => void removeMember(project.id, uid)}
                      className="icon-btn text-ink-400 hover:bg-red-950 hover:text-red-300"
                      aria-label={`Remove ${member.email ?? uid}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ) : (
                  <Badge tone="neutral">{ROLE_LABEL[member.role]}</Badge>
                )}
              </li>
            );
          })}
        </ul>
      </div>

      {!isOwner && (
        <p className="mt-4 text-xs text-ink-400">Only the owner can change who has access.</p>
      )}

      {user?.email && (
        <div className="mt-5 rounded-lg border border-ink-700 bg-ink-800/40 p-3">
          <p className="field-label mb-1">Your address for sharing</p>
          <div className="flex items-center gap-2">
            <code className="min-w-0 flex-1 truncate text-sm text-ink-200">{user.email}</code>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                navigator.clipboard?.writeText(user.email ?? '').then(
                  () => {
                    setCopiedSelf(true);
                    window.setTimeout(() => setCopiedSelf(false), 1800);
                  },
                  () => setError('Could not copy — select the address and copy it manually.'),
                );
              }}
            >
              {copiedSelf ? 'Copied' : 'Copy'}
            </Button>
          </div>
          <p className="mt-1.5 text-xs text-ink-400">
            Give this to whoever wants to share a project with you.
          </p>
        </div>
      )}
    </Modal>
  );
}
